import { Link } from "react-router-dom";
import { ArrowUpRight, Flame } from "lucide-react";
import Sticker from "@/components/site/Sticker";
import Marquee from "@/components/site/Marquee";
import WorkSlider from "@/components/site/WorkSlider";
import { SERVICES, SITE } from "@/data/site";

const STATS = [
  { value: "12+", label: "launches shipped" },
  { value: "4.8M", label: "organic views" },
  { value: "3", label: "formats that move" },
  { value: "24/7", label: "scroll-stopping energy" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ── HERO ── */}
      <section className="relative border-b border-zinc-900">
        <div className="grid grid-cols-1 md:grid-cols-12">
          <div className="md:col-span-8 xl:col-span-9 border-b md:border-b-0 md:border-r border-zinc-900 px-5 md:px-10 lg:px-12 pt-12 pb-10 lg:pt-20 lg:pb-16 overflow-hidden">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF1111]">
              Creative Media Agency • Pune, india
            </p>
            <h1
              data-testid="hero-title"
              className="font-display uppercase leading-[1.05] tracking-tighter mt-4 break-words"
              style={{ fontSize: "clamp(2.4rem, 6vw, 7rem)" }}
            >
              Creative that delivers
              <br />
              <span className="text-[#FF1111]">Create. Captivate.</span>
              <br />
              Convert.
            </h1>
            <p
              className="mt-6 max-w-2xl text-lg md:text-xl lg:text-2xl text-zinc-600 leading-relaxed"
              style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 400 }}
            >
              We create content, campaigns, and social media that capture attention, build trust, and drive growth.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 bg-[#FF1111] text-white px-5 py-3 md:px-6 md:py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-black transition-all duration-200 min-h-[44px]"
              >
                <Flame size={15} /> Work with us
                <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <Link
                to="/work"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-zinc-900 px-5 py-3 md:px-6 md:py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-zinc-900 hover:text-white transition-all duration-200 min-h-[44px]"
              >
                View our projects
              </Link>
            </div>
          </div>

          <div className="md:col-span-4 xl:col-span-3 bg-zinc-50 py-10 md:py-0">
            <div className="p-6 md:p-8 flex flex-col h-full items-center justify-center gap-6">
              <Sticker size={160} />
              <div className="text-center font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                <p>Est 2026 • Pune</p>
                <a
                  href={SITE.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block link-slash text-zinc-900"
                >
                  @{SITE.instagram}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee className="border-b border-zinc-900" />

      {/* ── STATS ── */}
      {/* <section className="border-b border-zinc-900 grid grid-cols-2 md:grid-cols-4 divide-x divide-zinc-900">
        {STATS.map((s, i) => (
          <div key={i} className="p-5 md:p-8 lg:p-10 border-b md:border-b-0 border-zinc-900">
            <p className="font-display text-4xl md:text-5xl lg:text-6xl uppercase leading-none">
              {s.value}
            </p>
            <p className="mt-2 font-grotesk text-xs md:text-sm text-zinc-700 leading-snug">{s.label}</p>
          </div>
        ))}
      </section> */}

      {/* ── SERVICES ── */}
      <section className="border-b border-zinc-900 bg-white">
        {/* Header */}
        <div className="px-5 md:px-10 lg:px-16 pt-16 pb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-zinc-900">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF1111]">Services</p>
            <h2 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.85] mt-3">
              What we do.
            </h2>
          </div>
          <p style={{ fontFamily: "'Space Grotesk', sans-serif" }} className="text-sm text-zinc-400 max-w-xs leading-relaxed">
            Four focused offerings. Built to move your brand forward.
          </p>
        </div>

        {/* Hover-reveal list */}
        <div>
          {SERVICES.map((s, i) => (
            <article
              key={s.code}
              data-testid={`service-${s.code}`}
              className="group border-b border-zinc-900 last:border-b-0 cursor-default"
            >
              <div className="grid grid-cols-[auto_1fr_auto] items-center gap-6 px-5 md:px-10 lg:px-16 py-6 md:py-8 transition-all duration-300 group-hover:pl-8 md:group-hover:pl-14 lg:group-hover:pl-20">
                {/* Animated red accent bar */}
                <div className="w-0 group-hover:w-8 md:group-hover:w-12 h-[2px] bg-[#FF1111] transition-all duration-500 ease-out" />

                {/* Number + title */}
                <div className="flex items-baseline gap-5 md:gap-8 min-w-0">
                  <span className="font-mono text-xs tracking-[0.25em] text-zinc-300 shrink-0 group-hover:text-[#FF1111] transition-colors duration-300">
                    {s.code}
                  </span>
                  <h3 className="font-display uppercase text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-none text-zinc-900 group-hover:text-zinc-900 transition-all duration-300 truncate">
                    {s.name}
                  </h3>
                </div>

                {/* Arrow */}
                <ArrowUpRight
                  size={22}
                  className="text-zinc-200 group-hover:text-[#FF1111] transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 shrink-0"
                />
              </div>

              {/* Description — slides in on hover */}
              <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-500 ease-out overflow-hidden">
                <div className="min-h-0 overflow-hidden">
                  <p
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    className="px-5 md:px-10 lg:px-16 pb-7 text-sm md:text-base text-zinc-500 leading-relaxed max-w-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100"
                  >
                    {s.desc}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── SELECTED WORK ── */}
      <section className="border-b border-zinc-900">
        <div className="px-5 md:px-10 pt-8 pb-6">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FF1111]">
            Selected work
          </p>
          <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-7xl leading-[0.88] mt-3">
            Campaigns that
            <br />
            <span className="text-[#FF1111]">land hard.</span>
          </h2>
          <Link
            to="/work"
            data-testid="home-work-cta"
            className="mt-5 inline-flex items-center gap-2 border border-zinc-900 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-zinc-900 hover:text-white transition-all duration-200 min-h-[44px] whitespace-nowrap"
          >
            Explore our work <ArrowUpRight size={14} />
          </Link>
        </div>
        <WorkSlider displayHeading />
      </section>
    </div>
  );
}
